import { config } from '@brands/config/config';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';

/**
 * Determines whether a user should be excluded from Customer.io.
 *
 * Rules:
 * - Exclude anyone who is not a Patient. Customer.io is the patient marketing
 *   platform; identifying a provider, care advisor or admin creates a person
 *   there who can then be swept into patient broadcasts and automations.
 * - Exclude if the backend's computed `disable_customerio` flag is set (true only
 *   when the user has client tags and every one of them has disable_customerio
 *   enabled) — this is the source of truth per client, independent of the
 *   static FE exclusion lists below.
 * - Exclude if the user's primary client tag (clients[0]) has disable_customerio
 *   set directly — covers the case where the backend hasn't (yet, or won't)
 *   aggregate that flag onto the user record itself.
 * - Exclude if user has exactly 1 client tag AND that tag exists in excludedCioClientTags.
 * - Exclude if user has multiple tags AND all of them exist in excludedCioClientTags.
 * - Exclude if ANY of the user's organizations exist in excludedCioOrganizations.
 */
export function shouldExcludeFromCio(user: UserProfile): boolean {
  if (user?.role?.name !== UserRoleName.Patient) {
    return true;
  }

  if (user?.disable_customerio) {
    return true;
  }

  if (user?.clients?.[0]?.disable_customerio) {
    return true;
  }

  const excludedIds: string[] = config.excludedCioClientTags || [];
  const excludedCioOrganizationIds: string[] = config.excludedCioOrganizations || [];

  const tags = user?.clients || [];
  const organizations = user?.organizations || [];

  // Exclude if any organization is in the exclusion list
  const belongsToExcludedOrg = organizations.some((org) => excludedCioOrganizationIds.includes(org.id || ''));
  if (belongsToExcludedOrg) {
    return true;
  }

  // Single tag → exclude if that tag is in the exclusion list
  if (tags.length === 1) {
    return excludedIds.includes(tags[0].code || '');
  }

  // Multiple tags → exclude only if all tags are in the exclusion list
  if (tags.length > 1) {
    return tags.every((tag) => excludedIds.includes(tag.code || ''));
  }

  return false;
}
